import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Check, X, Save, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const CLASSES = ["Playgroup", "Nursery", "LKG", "UKG"];

export default function Attendance() {
  const { user } = useAuth();
  const [dateStr, setDateStr] = useState(new Date().toISOString().slice(0, 10));
  const [cls, setCls] = useState(user?.assigned_class || "Nursery");
  const [children, setChildren] = useState([]);
  const [records, setRecords] = useState({}); // {childId: "Present"|"Absent"}
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.role === "teacher" && user?.assigned_class) setCls(user.assigned_class);
  }, [user]);

  const load = async () => {
    const [c, a] = await Promise.all([
      api.get("/children", { params: { class_name: cls } }),
      api.get("/attendance", { params: { date: dateStr, class_name: cls } }),
    ]);
    setChildren(c.data);
    const map = {};
    a.data.forEach((r) => { map[r.child_id] = r.status; });
    setRecords(map);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateStr, cls]);

  const set = (cid, status) => setRecords({ ...records, [cid]: status });

  const saveAll = async () => {
    setSaving(true);
    try {
      const entries = children.map((c) => ({
        child_id: c.id,
        date: dateStr,
        status: records[c.id] || "Present",
      }));
      await api.post("/attendance/bulk", { date: dateStr, class_name: cls, entries });
      toast.success("Attendance saved");
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const markAllPresent = () => {
    const map = {};
    children.forEach((c) => { map[c.id] = "Present"; });
    setRecords(map);
  };

  const stats = useMemo(() => {
    const p = Object.values(records).filter((s) => s === "Present").length;
    const a = Object.values(records).filter((s) => s === "Absent").length;
    return { p, a, unmarked: children.length - p - a };
  }, [records, children]);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-black">Attendance</h1>
        <p className="text-muted-foreground font-medium">One tap per child. Fast and easy.</p>
      </header>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 card-soft rounded-full px-4 py-2">
          <CalendarDays size={18} className="text-primary" />
          <Input data-testid="att-date" type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} className="border-0 h-9 p-0 focus-visible:ring-0 font-semibold w-36" />
        </div>
        {user?.role !== "teacher" && (
          <Select value={cls} onValueChange={setCls}>
            <SelectTrigger data-testid="att-class" className="h-11 rounded-full w-40"><SelectValue /></SelectTrigger>
            <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Button variant="outline" size="sm" onClick={markAllPresent} className="rounded-full">All Present</Button>
        <div className="flex gap-2 ml-auto text-sm font-semibold">
          <span className="chip-green px-3 py-1 rounded-full">Present {stats.p}</span>
          <span className="chip-pink px-3 py-1 rounded-full">Absent {stats.a}</span>
          {stats.unmarked > 0 && <span className="chip-amber px-3 py-1 rounded-full">Unmarked {stats.unmarked}</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {children.map((c) => {
          const st = records[c.id];
          return (
            <div key={c.id} className="card-soft rounded-2xl p-4 flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl chip-lilac flex items-center justify-center font-black">
                {c.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-extrabold truncate">{c.name}</div>
                <div className="text-xs text-muted-foreground">{c.class_name}</div>
              </div>
              <button
                data-testid={`present-${c.id}`}
                onClick={() => set(c.id, "Present")}
                className={`tap-btn px-4 flex items-center gap-1 ${st === "Present" ? "bg-primary text-primary-foreground shadow" : "chip-green"}`}
              >
                <Check size={16} /> Present
              </button>
              <button
                data-testid={`absent-${c.id}`}
                onClick={() => set(c.id, "Absent")}
                className={`tap-btn px-4 flex items-center gap-1 ${st === "Absent" ? "bg-destructive text-destructive-foreground shadow" : "chip-pink"}`}
              >
                <X size={16} /> Absent
              </button>
            </div>
          );
        })}
        {children.length === 0 && (
          <div className="col-span-full text-center py-12 text-muted-foreground">No children in this class yet.</div>
        )}
      </div>

      {children.length > 0 && (
        <div className="sticky bottom-4 flex justify-end">
          <Button data-testid="save-attendance" disabled={saving} onClick={saveAll} className="rounded-full h-14 px-8 text-base font-bold shadow-lg hover:-translate-y-0.5 transition-all">
            <Save size={18} /> {saving ? "Saving..." : "Save Attendance"}
          </Button>
        </div>
      )}
    </div>
  );
}
